import { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { isEditableTarget, shouldShieldKeyboardEvent } from '../../commands/commandModel.js'

const INTERVALS = Object.freeze({
  1: { timeframe: '1min', description: '1 minute' },
  2: { timeframe: '2min', description: '2 minutes' },
  5: { timeframe: '5min', description: '5 minutes' },
  15: { timeframe: '15min', description: '15 minutes' },
  30: { timeframe: '30min', description: '30 minutes' },
  65: { timeframe: '65min', description: '65 minutes' },
  D: { timeframe: 'daily', description: '1 day' },
  '1D': { timeframe: 'daily', description: '1 day' },
  W: { timeframe: 'weekly', description: '1 week' },
  '1W': { timeframe: 'weekly', description: '1 week' },
})

const INTERACTIVE_SELECTOR = 'button, input, textarea, select, [contenteditable="true"], [role="button"]'

function normalizeDraft(value) {
  return String(value || '').replace(/[^0-9a-z]/gi, '').toUpperCase().slice(0, 4)
}

function isPlainCharacter(event) {
  return !event.metaKey && !event.ctrlKey && !event.altKey
    && /^[0-9a-z]$/i.test(String(event.key || ''))
}

function isVisibleSurface(node) {
  if (!node?.isConnected) return false
  if (typeof node.getClientRects !== 'function') return true
  return node.getClientRects().length > 0
}

export default function ChartIntervalCommandSurface({ children, className = '', onTimeframeChange, disabledTimeframes = [], disabled = false }) {
  const surfaceRef = useRef(null)
  const inputRef = useRef(null)
  const returnFocusRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const match = useMemo(() => INTERVALS[draft] || null, [draft])

  useEffect(() => {
    if (disabled) { setOpen(false); setDraft('') }
  }, [disabled])

  useEffect(() => {
    if (!open) return undefined
    const frame = requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }))
    return () => cancelAnimationFrame(frame)
  }, [open])

  useEffect(() => {
    if (open || disabled) return undefined

    const handleWorkspaceKeyDown = (event) => {
      if (!isPlainCharacter(event)) return
      if (!isVisibleSurface(surfaceRef.current)) return
      const key = String(event.key).toUpperCase()
      if (!/^[0-9DW]$/.test(key)) return
      // Workspace-wide entry intentionally includes ordinary buttons and
      // chart controls. Only editors, command shields, and modals own these
      // plain interval keys.
      if (
        shouldShieldKeyboardEvent(event)
        || isEditableTarget(document.activeElement)
        || document.querySelector('[aria-modal="true"]')
      ) return

      event.preventDefault()
      event.stopImmediatePropagation()
      returnFocusRef.current = document.activeElement === document.body
        ? surfaceRef.current
        : document.activeElement
      setDraft(key)
      setOpen(true)
    }

    window.addEventListener('keydown', handleWorkspaceKeyDown)
    return () => window.removeEventListener('keydown', handleWorkspaceKeyDown)
  }, [open, disabled])

  const closeAndRefocus = () => {
    setOpen(false)
    setDraft('')
    requestAnimationFrame(() => {
      const target = returnFocusRef.current?.isConnected
        ? returnFocusRef.current
        : surfaceRef.current
      target?.focus?.({ preventScroll: true })
      returnFocusRef.current = null
    })
  }

  const commit = () => {
    if (disabled || !match || disabledTimeframes.includes(match.timeframe)) return
    onTimeframeChange(match.timeframe)
    closeAndRefocus()
  }

  const handleSurfacePointerDown = (event) => {
    if (event.target?.closest?.(INTERACTIVE_SELECTOR)) return
    surfaceRef.current?.focus({ preventScroll: true })
  }

  const handleInputKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      closeAndRefocus()
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      commit()
      return
    }
    if (event.key === 'Backspace') {
      event.preventDefault()
      event.stopPropagation()
      setDraft((current) => current.slice(0, -1))
      return
    }
    if (!isPlainCharacter(event)) return
    event.preventDefault()
    event.stopPropagation()
    setDraft((current) => normalizeDraft(`${current}${event.key}`))
  }

  return (
    <div
      ref={surfaceRef}
      data-testid="chart-interval-command-surface"
      role="region"
      aria-label="Chart keyboard controls"
      tabIndex={0}
      onPointerDown={handleSurfacePointerDown}
      className={`${className} outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink/35`}
    >
      {children}
      {open && !disabled && (
        <div
          className="absolute inset-0 z-40 flex items-start justify-center bg-black/5 px-4 pt-[8%] backdrop-blur-[1px]"
          onPointerDown={(event) => {
            if (event.target !== event.currentTarget) return
            event.preventDefault()
            event.stopPropagation()
            closeAndRefocus()
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="chart-interval-title"
            data-command-shield="true"
            className="w-full max-w-sm rounded-xl border border-border bg-canvas p-6 text-ink shadow-2xl"
          >
            <h2 id="chart-interval-title" className="font-sans text-xl font-semibold">Change interval</h2>
            <input
              ref={inputRef}
              aria-label="Chart interval"
              value={draft}
              inputMode="text"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              onChange={(event) => setDraft(normalizeDraft(event.target.value))}
              onKeyDown={handleInputKeyDown}
              className="mt-5 w-full rounded-lg border border-ink/35 bg-canvas2 px-4 py-3 text-center font-mono text-2xl text-ink outline-none focus:border-ink focus:ring-2 focus:ring-ink/20"
            />
            <div
              aria-live="polite"
              className={`mt-3 text-center font-sans text-sm ${match ? 'text-inkMuted' : 'text-ember'}`}
            >
              {match?.description || 'Interval unavailable'}
            </div>
            <div className="mt-5 text-center font-mono text-[10px] uppercase tracking-wider text-inkMuted">
              Enter to apply · Esc to cancel
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

ChartIntervalCommandSurface.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  onTimeframeChange: PropTypes.func.isRequired,
  disabledTimeframes: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
}
